import React, { useEffect } from 'react';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';

import { ConfettiOverlay } from 'Components/confetti';
import { useServiceFeeEnabled } from 'Components/transactionFees/hooks/useServiceFeeEnabled';
import { VehicleCard } from 'Storybook/components/local/VehicleCard';

import SoldTag from '../../../../../client/assets_seller/documentCapture/sold-tag.png';
import { useBreakpoint } from '../../../../components/misc/Breakpoints/Breakpoints';
import PageLoader from '../../../../components/transitions/PageLoader/PageLoader';
import { DealerRatingsExperiment } from '../../experiments/DealerRatingsExperiment/DealerRatingsExperiment';
import { getIsReservePriceMet } from '../../experiments/DealerRatingsExperiment/DealerRatingsExperiment.helpers';
import { useDealerRatingsExperiment } from '../../experiments/DealerRatingsExperiment/useDealerRatingsExperiment';
import { SellerConfirmationExperiment } from '../../experiments/SellerConfirmation/SellerConfirmationExperiment';
import { useSimilarVehiclesDataGathering } from '../../experiments/useSimilarVehiclesDataGathering/useSimilarVehiclesDataGathering';
import { useUnifiedSellerAcceptanceAboveReserveExperiment } from '../../experiments/useUnifiedSellerAcceptanceAboveReserveExperiment/useUnifiedSellerAcceptanceAboveReserveExperiment';
import { useValuationLoaderLocalStorage } from '../../fastlane/valuations/components/ValuationLoader/useValuationLoaderLocalStorage';
import { useVehicleCollectionSub } from '../../vehicleCollection/hooks/useVehicleCollectionSub';
import { useGetOfferBids } from '../useGetOfferBids';

import { Footer } from './Footer/Footer';
import { OfferSummaryContextProvider, useOfferSummary } from './hooks/useOfferSummary';
import { InfoList } from './InfoList/InfoList';
import { TrustPilotReviews } from './TrustPilotReviews/TrustPilotReviews';

import styles from './OfferSummary.styles.scss';

const { CONFIRM_SALE_DECISIONS, CONFIRM_SALE_PAGE_LOAD } = DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS;

export const OfferSummary = () => {
  const {
    dpVehicleId,
    isEnquiryLoaded,
    offer,
    seller: { isBereavement },
    vehicle: { image, vrmPretty, ...vehicleDetails },
    vehicleEnquiry,
  } = useOfferSummary();
  const { removeEnquiry: removeValuationLoaderEnquiry } = useValuationLoaderLocalStorage();

  // This should be replaced by adding closingBidValue to useMotorwayOffer hook response
  const { loading: offerBidsLoading, offer: offerBids } = useGetOfferBids({
    dpEnquiryId: offer?.offerId,
    vrm: vehicleDetails?.vrm,
  }) ?? {};

  const { isServiceFeeEnabled } = useServiceFeeEnabled({
    dpEnquiryId: offer?.offerId,
  });

  const { serviceFee, serviceFeeTiers } = useVehicleCollectionSub();

  useSimilarVehiclesDataGathering(dpVehicleId, {
    make: vehicleDetails?.make,
    mileage: vehicleDetails?.mileage,
    model: vehicleDetails?.model,
    price: offer?.acceptedBidValue,
    year: vehicleDetails?.manufactureYear,
  });

  const { decided, isEligibleForDealerRatingsExperiment } = useDealerRatingsExperiment({
    offer: offerBids ?? undefined,
    vehicleEnquiry: vehicleEnquiry ?? undefined,
  });

  const isReservePriceMet = getIsReservePriceMet({
    offer: offerBids ?? undefined,
    vehicleEnquiry: vehicleEnquiry ?? undefined,
  });

  const isInAboveReserveExperiment = useUnifiedSellerAcceptanceAboveReserveExperiment({
    dpEnquiryId: offer?.offerId,
    isEligible: decided && !isEligibleForDealerRatingsExperiment && isReservePriceMet,
  });

  // Temporary event to establish why sellers are getting through
  useEffect(() => {
    if (offerBids && vehicleEnquiry && decided) {
      CONFIRM_SALE_DECISIONS({
        closingBidValue: offerBids?.closingBid?.value ?? null,
        dealerId: offerBids?.closingBid?.dealerContact?.dealer?.id ?? null,
        dpEnquiryId: offer?.offerId ?? null,
        isEligibleForDealerRatingsExperiment,
        isInAboveReserveExperiment,
        isReservePriceMet,
        prepCosts: vehicleEnquiry.prepCosts,
        priceHigh: vehicleEnquiry.priceHigh,
        sellerWillAcceptPrice: vehicleEnquiry.sellerWillAcceptPrice,
      });
    }
  }, [
    decided,
    offerBids,
    vehicleEnquiry,
    isReservePriceMet,
    isEligibleForDealerRatingsExperiment,
    isInAboveReserveExperiment,
    offer?.offerId,
  ]);

  const { maxWidth } = useBreakpoint();

  const isMobile = maxWidth.breakpointMobile;

  const badge = <img src={SoldTag} width="80" />;

  useEffect(() => {
    if (isEnquiryLoaded) {
      CONFIRM_SALE_PAGE_LOAD();
    }
  }, [isEnquiryLoaded]);

  useEffect(() => {
    removeValuationLoaderEnquiry();
  }, [removeValuationLoaderEnquiry]);

  if (!isEnquiryLoaded || offerBidsLoading || !decided) {
    // I really don't like this but with the current route wrappers it was either adding the loader into the page
    // despite being inside the wrapping layout components OR add the enquiry call to every document capture route.
    return (
      <div className={styles.loaderWrapper}>
        <PageLoader />
      </div>
    );
  }

  if (isInAboveReserveExperiment && vehicleEnquiry && offerBids) {
    return <SellerConfirmationExperiment {...{
      isServiceFeeEnabled,
      offer: offerBids,
      serviceFeeDetails: {
        serviceFee,
        serviceFeeTiers,
      },
      vehicleEnquiry,
    }} />;
  }

  return (
    <>
      {!isBereavement && <ConfettiOverlay />}
      <>
        <div className={styles.infoContainer}>
          <div className={styles.saleInfoContainer}>
            <InfoList
              {...{ isServiceFeeEnabled }}
              closingBidValue={offerBids?.closingBid?.value!}
            >
            </InfoList>
            <TrustPilotReviews />
          </div>
          <div className={styles.vehicleInfoContainer}>
            <VehicleCard
              badge={badge}
              image={image as string}
              showVehicleInfo={!isMobile}
              vehicleDetails={vehicleDetails}
              vrm={vrmPretty}
            />
          </div>
        </div>
        <Footer />
        {
          offerBids
        && vehicleEnquiry
        && <DealerRatingsExperiment { ...{ offer: offerBids, vehicleEnquiry } }/>
        }
      </>
    </>
  );
};

export const OfferSummaryWithContext = () => (
  <OfferSummaryContextProvider>
    <OfferSummary />
  </OfferSummaryContextProvider>
);
